import type { EntityPL, PLAccount, PLSection, PLSubtotal, PLTotals, ProfitLossData } from '../types'

// =============================================================================
// Consolidated (€) view — combines the Spain (EUR) and US (USD) blocks into one
// € statement, mirroring the Sales Report's consolidate step.
//
// FX method (IAS 21 / ASC 830 management practice):
//   • Every monthly flow figure (each account line, each section total) is
//     converted at THAT month's locked average rate, then summed with Spain.
//   • Margin %s are NEVER FX-averaged — they are recomputed from the converted
//     absolute totals, so the consolidated margin is the true blended margin.
//   • Business unit / department Income subtotals (schema v2) follow the same
//     rule as the Income lines: each month converted at its own rate, summed with
//     Spain, YTD = the sum of the converted YTD months. So the consolidated unit
//     subtotals add up to the consolidated Income total (within cent rounding).
// Spain is a "data pending" placeholder today, so the consolidated figures
// currently reflect the US block only; the maths is already entity-agnostic, so
// Spain's numbers fold in automatically once its sheets arrive.
// =============================================================================

/** Convert a USD amount to EUR. `rate` is USD per 1 EUR, so EUR = USD / rate. */
const toEur = (usd: number, rate: number) => (rate ? usd / rate : 0)

const round2 = (x: number) => Math.round(x * 100) / 100
const sumFrom = (a: number[], start: number) => round2(a.slice(start).reduce((x, y) => x + y, 0))

/** First index of the fiscal YTD window (the first month of `ytdYear`). */
function ytdStartIndex(months: string[], ytdYear: number): number {
  const suffix = `-${String(ytdYear).slice(2)}`
  const i = months.findIndex((m) => m.endsWith(suffix))
  return i < 0 ? months.length : i
}

/** Merge one section: Spain (native EUR) + US (converted per month). Accounts are
 *  unioned by id, Spain order first, then any US-only accounts appended. */
function mergeSection(
  es: PLSection | undefined,
  us: PLSection | undefined,
  months: string[],
  rateAt: (m: string) => number,
  ytdIdx: number,
): PLSection {
  const base = (es ?? us)!
  const order: string[] = []
  const map = new Map<string, PLAccount>()

  const add = (sec: PLSection | undefined, convert: boolean) => {
    sec?.accounts.forEach((a) => {
      if (!map.has(a.id)) {
        order.push(a.id)
        map.set(a.id, { id: a.id, name: a.name, values: months.map(() => 0), ytd: 0 })
      }
      const tgt = map.get(a.id)!
      months.forEach((m, i) => {
        tgt.values[i] += convert ? toEur(a.values[i] ?? 0, rateAt(m)) : a.values[i] ?? 0
      })
    })
  }
  add(es, false)
  add(us, true)

  const accounts = order.map((id) => {
    const a = map.get(id)!
    a.values = a.values.map(round2)
    a.ytd = sumFrom(a.values, ytdIdx)
    return a
  })

  const total = months.map((m, i) => round2((es?.total[i] ?? 0) + toEur(us?.total[i] ?? 0, rateAt(m))))
  return { id: base.id, label: base.label, kind: base.kind, total, totalYtd: sumFrom(total, ytdIdx), accounts }
}

/** Merge Spain (native EUR) + US (converted per month) subtotals. Rows are
 *  unioned by `keyOf`, Spain order first, then any US-only rows appended (e.g. the
 *  US "Other products (unsplit) / bridge" unit). Account ids are unioned too. */
function mergeSubtotals(
  es: PLSubtotal[] | undefined,
  us: PLSubtotal[] | undefined,
  keyOf: (s: PLSubtotal) => string,
  months: string[],
  rateAt: (m: string) => number,
  ytdIdx: number,
): PLSubtotal[] {
  const order: string[] = []
  const map = new Map<string, PLSubtotal>()

  const add = (list: PLSubtotal[] | undefined, convert: boolean) => {
    list?.forEach((s) => {
      const key = keyOf(s)
      if (!map.has(key)) {
        order.push(key)
        map.set(key, { id: s.id, name: s.name, ...(s.unit ? { unit: s.unit } : {}), values: months.map(() => 0), accounts: [], ytd: 0 })
      }
      const tgt = map.get(key)!
      months.forEach((m, i) => {
        tgt.values[i] += convert ? toEur(s.values[i] ?? 0, rateAt(m)) : s.values[i] ?? 0
      })
      ;(s.accounts ?? []).forEach((a) => {
        if (!tgt.accounts.includes(a)) tgt.accounts.push(a)
      })
    })
  }
  add(es, false)
  add(us, true)

  return order.map((key) => {
    const s = map.get(key)!
    s.values = s.values.map(round2)
    s.ytd = sumFrom(s.values, ytdIdx)
    return s
  })
}

/** An entity carries the schema-v2 subtotals, or has no Income to subtotal. */
const subtotalsReady = (pl: EntityPL) =>
  pl.unitSubtotals !== undefined || !pl.sections.some((s) => s.id === 'income' && s.accounts.length > 0)

/** Build the consolidated (€) EntityPL from Spain + US at the locked rates. */
export function buildConsolidated(report: ProfitLossData): EntityPL {
  const spain = report.data.spain
  const us = report.data.us
  const months = report.months
  const rateAt = (m: string) => report.fxRates[m] ?? 1
  const ytdIdx = ytdStartIndex(months, report.ytdYear)

  // Canonical section order comes from whichever entity is populated.
  const order = (spain.sections.length ? spain.sections : us.sections).map((s) => s.id)
  const eById = new Map(spain.sections.map((s) => [s.id, s]))
  const uById = new Map(us.sections.map((s) => [s.id, s]))
  const sections = order.map((id) => mergeSection(eById.get(id), uById.get(id), months, rateAt, ytdIdx))

  const secTotal = (id: string) => sections.find((s) => s.id === id)?.total ?? months.map(() => 0)
  const revenue = secTotal('income')
  const cogs = secTotal('cogs')
  const opex = secTotal('opex')
  const other = secTotal('otherExpense')

  const grossProfit = revenue.map((v, i) => round2(v - cogs[i]))
  const operatingProfit = grossProfit.map((v, i) => round2(v - opex[i]))
  const netProfit = operatingProfit.map((v, i) => round2(v - other[i]))
  const grossMarginPct = revenue.map((v, i) => (v ? round2((grossProfit[i] / v) * 100) : 0))
  const netMarginPct = revenue.map((v, i) => (v ? round2((netProfit[i] / v) * 100) : 0))
  const whole = (a: number[]) => round2(a.reduce((x, y) => x + y, 0))

  const totals: PLTotals = {
    revenue,
    cogs,
    grossProfit,
    grossMarginPct,
    opex,
    operatingProfit,
    otherExpense: other,
    netProfit,
    netMarginPct,
    revenueYtd: sumFrom(revenue, ytdIdx),
    cogsYtd: sumFrom(cogs, ytdIdx),
    grossProfitYtd: sumFrom(grossProfit, ytdIdx),
    opexYtd: sumFrom(opex, ytdIdx),
    operatingProfitYtd: sumFrom(operatingProfit, ytdIdx),
    otherExpenseYtd: sumFrom(other, ytdIdx),
    netProfitYtd: sumFrom(netProfit, ytdIdx),
    fullPeriod: {
      revenue: whole(revenue),
      cogs: whole(cogs),
      grossProfit: whole(grossProfit),
      opex: whole(opex),
      operatingProfit: whole(operatingProfit),
      otherExpense: whole(other),
      netProfit: whole(netProfit),
    },
  }

  // Income by business unit › department. Undefined (the v1 notice) unless every
  // entity with Income carries the schema-v2 subtotals.
  const v2 = subtotalsReady(spain) && subtotalsReady(us)
  const unitSubtotals = v2
    ? mergeSubtotals(spain.unitSubtotals, us.unitSubtotals, (s) => s.id, months, rateAt, ytdIdx)
    : undefined
  const departmentSubtotals = v2
    ? mergeSubtotals(spain.departmentSubtotals, us.departmentSubtotals, (s) => `${s.unit ?? ''}|${s.id}`, months, rateAt, ytdIdx)
    : undefined

  return { sections, totals, unitSubtotals, departmentSubtotals }
}

/** Current-month rate (USD per 1 EUR) for display in the consolidated note. */
export function currentRate(report: ProfitLossData): number {
  return report.fxRates[report.months[report.months.length - 1]] ?? 1
}
